import React, { useCallback, useRef, useState } from 'react';

import {
    Autocomplete,
    AutocompleteChangeReason,
    TextField,
    createFilterOptions,
} from '@mui/material';
import { union } from '@npi/buddy-utils';

import { showErrorToast } from 'src/components/Toasts/toast';

import { normalizeEmail, validateEmailForAddition } from './utils/validations';

export type EmailRecipientsInputProps = {
    setSendRecipients: React.Dispatch<React.SetStateAction<string[]>>;
    sendRecipients: string[];
};

const filter = createFilterOptions<string>();

export const EmailRecipientsInput: React.FC<EmailRecipientsInputProps> = ({
    sendRecipients,
    setSendRecipients,
}: EmailRecipientsInputProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [currentlyTypingEmail, setCurrentlyTypingEmail] = useState<string>('');

    const handleAddEmail = useCallback(
        async (emailToAdd: string) => {
            const normalizedEmail = normalizeEmail(emailToAdd);
            const validation = await validateEmailForAddition(normalizedEmail, sendRecipients);

            const { isValid, error } = validation;

            if (!isValid) {
                showErrorToast(error);
                setCurrentlyTypingEmail('');
                return;
            }

            setSendRecipients((prevState: string[]) => {
                const updated = union([normalizedEmail], prevState);
                return updated;
            });
            setCurrentlyTypingEmail('');
        },
        [sendRecipients, setSendRecipients]
    );

    const handleChangeRecipients = useCallback(
        (event: any, value: any, reason: AutocompleteChangeReason) => {
            event?.preventDefault();
            event?.stopPropagation();

            if (reason === 'removeOption') {
                if (event.code === 'Backspace') {
                    if (value.length === 1) {
                        setSendRecipients([]);
                        return;
                    }
                    const newValue = value.slice(0, value.length - 1);
                    setSendRecipients(newValue);
                    return;
                }

                setSendRecipients(value);
                return;
            }

            if (reason === 'clear') {
                setSendRecipients([]);
                setCurrentlyTypingEmail('');
                return;
            }
        },
        [setSendRecipients]
    );

    const handleKeyPress = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const target = event.target as HTMLInputElement;
            const value = target.value.trim();

            if (event.code === 'Space' || event.code === 'Enter') {
                event.preventDefault();
                handleAddEmail(value);
                return;
            }

            if (event.code === 'Backspace') {
                if (currentlyTypingEmail?.length === 0 && sendRecipients.length > 0) {
                    setSendRecipients(sendRecipients.slice(0, -1));
                }
            }
        },
        [currentlyTypingEmail, sendRecipients, handleAddEmail, setSendRecipients]
    );

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setCurrentlyTypingEmail(e.target.value);
    }, []);

    const handleBlur = useCallback(() => {
        if (currentlyTypingEmail.trim()) {
            handleAddEmail(currentlyTypingEmail.trim());
        }
    }, [currentlyTypingEmail, handleAddEmail]);

    return (
        <Autocomplete
            sx={{
                '&.MuiAutocomplete-root': {
                    '& fieldset': {
                        borderColor: '#F39E4E',
                        borderWidth: '1px',
                    },
                    '&:hover fieldset': {
                        borderColor: '#F39E4E',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: '#F39E4E',
                    },
                },
            }}
            open={false}
            value={sendRecipients}
            multiple
            options={sendRecipients}
            freeSolo
            id="recipients"
            onChange={handleChangeRecipients}
            filterOptions={(options, params) => {
                const filtered = filter(options, params);
                return filtered;
            }}
            ListboxProps={{
                style: { maxHeight: '300px', color: '#000' },
            }}
            getOptionLabel={(option) => {
                return option;
            }}
            renderOption={(props, option) => {
                return <li {...props}>{option}</li>;
            }}
            renderInput={(params) => {
                return (
                    <TextField
                        sx={{
                            '& label': {
                                zIndex: 10,
                            },
                            '&': {
                                flexGrow: 1,
                            },
                        }}
                        inputRef={inputRef}
                        {...params}
                        inputProps={{
                            ...params.inputProps,
                            onKeyDown: handleKeyPress,
                            value: currentlyTypingEmail,
                            'data-testid': 'recipients-input',
                        }}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        label="Recipient(s)"
                    />
                );
            }}
        />
    );
};
